import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Messages } from 'src/app/order/messages/order.messages';
import { ProposalService } from 'src/app/order/services/external/proposal/proposal.service';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { WizardService } from 'src/app/order/services/internal/wizard/wizard.service';
import { DataStore } from 'src/app/order/store/data.store';
import { Proposal } from 'src/app/shared/models/proposal';
import { FdFieldConfigs } from 'src/app/shared/shared-components.module';
import { Item } from 'src/app/shared/fd-form-components/fd-select/fd-select.component';
import { ConfigurationService } from 'src/app/start/services/configuration.service';
import { FormStep } from '../../../form-step';

import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { UrlModel } from '../../../../../shared/models/url.model';
import { UrlService } from '../../../../services/external/url/url.service';
import { MatTooltip } from '@angular/material/tooltip';
import { EcommerceComplementaryInfoModel } from '../models/complementary-info.model';
import { EcommerceTechTypeEnum } from 'src/app/order/enums/ecommerce-tech-type.enum';
import { ECommerceFunctionalitiesEnum } from 'src/app/order/components/complementary-information/e-commerce/models/e-commerce-funcionalities.model';
import { CurrencyValidator } from 'src/app/order/validators/currency-validator';

@Component({
  selector: 'app-carat',
  templateUrl: './carat.component.html',
  styleUrls: ['./carat.component.scss'],
  standalone: false
})
export class CaratComponent extends FormStep implements OnInit {

  public STEP_NUMBER = 0;

  public techType = EcommerceTechTypeEnum.ECC_CARAT;

  public caratModel: EcommerceComplementaryInfoModel;

  public extraFunctionalitiesList: Item[] = [
    {
      label: 'Recorrência',
      value: ECommerceFunctionalitiesEnum.RECURRENCE,
      selected: false,
    },
    {
      label: '3DS',
      value: ECommerceFunctionalitiesEnum.THREE_DS,
      selected: false,
    },
    {
      label: 'Antifraude',
      value: ECommerceFunctionalitiesEnum.ANTI_FRAUD,
      selected: false,
    },
    {
      label: 'Tokenização',
      value: ECommerceFunctionalitiesEnum.TOKENIZATION,
      selected: false,
    },
  ];

  private transactionRiskAwareModalAccept: boolean = false;

  private readonly TRANSACTION_RISK_AWARE_MODAL_MESSAGE = `
    <ul>
      <li>A venda com pagamento online requer uma reserva para antecipação de recebíveis.</li>
      <li>As perdas podem ocorrer nos casos de transações fraudulentas e contestações de
      transações por parte do consumidor, gerando estorno ou chargeback.</li>
      <li>Oriente o cliente a guardar e-mails,
      WhatsApp, notas fiscais, recibos e comprovantes de entrega pois estes documentos podem
      ser solicitados em caso de disputa.</li>
    </ul>
  `;

  @ViewChild('tooltip') public tooltip: MatTooltip;

  constructor(
    private formBuilder: FormBuilder,
    protected proposalService: ProposalService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected dataStore: DataStore,
    protected scrollService: ScrollService,
    protected wizardService: WizardService,
    protected matDialog: MatDialog,
    protected configurationService: ConfigurationService,
    protected routingService: RoutingService,
    protected dataStoreService: DataStoreService,
    protected urlService: UrlService
  ) {
    super(
      dataStore,
      scrollService,
      wizardService,
      matDialog,
      proposalService,
      loadingService,
      dialogService,
      configurationService,
      routingService,
      dataStoreService
    );
  }

  public get hasAntiFraudService(): boolean {
    return !!this.formControls.hasAntiFraudService.value;
  }

  public async ngOnInit() {
    await super.ngOnInit();
    this.subscribeToTransactionRiskAware();
  }

  public getGatewayTooltipText() {
    return 'Descrever com detalhes os tipos de produtos/serviços comercializados. Não é o CNAE.';
  }

  public showTooltipAndStopPropagation() {
    this.tooltip.toggle();
    event.stopImmediatePropagation();
  }

  public validateUrl(url) {
    if (url && url !== '') {
      const urlModel: UrlModel = new UrlModel(url);
      this.urlService.validateStructure(urlModel).subscribe(
        (validStructureUrl) => {
          if (!validStructureUrl) {
            this.formControls.link.setValue('');
            this.dialogService.openDialog(Messages.INVALID_STRUCTURE_URL);
          }
        },
        () => {
          this.formControls.link.setValue('');
          this.dialogService.openDialog(Messages.GENERAL_ERROR);
        }
      );
    }
  }

  public onExtraFunctionalitiesCheckboxChange(item: Item, event: MatCheckboxChange) {
    item.selected = event.checked;

    this.formControls.extraFunctionalities.setValue(
      this.extraFunctionalitiesList.filter((x) => x.selected).map((x) => x.value)
    );
  }

  public next(): void {
    this.submitted = true;

    if (this.isFormGroupValid()) {
      this.dataStore.setStepStatus(this.STEP_NUMBER, this.formGroup.valid);
      this.persistData().then((persisted) => {
        if (persisted) {
          this.wizardService.nextStep();
        }
      });
    }
  }

  public persistData() {
    const caratComplementaryInfo: EcommerceComplementaryInfoModel = {
      techType: this.techType,
      technicalContact: this.caratModel?.technicalContact,
      ...this.formGroup.getRawValue(),
    };

    const ecommerceComplementaryInfo = (this.proposal?.ecommerceComplementaryInfo || [])
      .filter((complementaryInfo) => complementaryInfo.techType !== this.techType);

    ecommerceComplementaryInfo.push(caratComplementaryInfo);

    return this.dataStore.updateProposal({ ecommerceComplementaryInfo });
  }

  public createFormGroup() {
    return this.formBuilder.group({
      extraFunctionalities: [[]],
      link: ['', Validators.required],
      productOrServiceDetails: ['', Validators.required],
      describeHowWillBeDelivered: ['', Validators.required],
      maxValueOfTransaction: ['', [Validators.required, CurrencyValidator(0.01)]],
      hasAntiFraudService: ['', Validators.required],
      transactionRiskAware: ['', Validators.required]
    });
  }

  protected proposalGetCallback(): void {}

  protected updateFields(proposal: Proposal) {
    if (proposal.ecommerceComplementaryInfo) {
      this.caratModel = proposal.ecommerceComplementaryInfo
          .filter((complementaryInfo) => complementaryInfo.techType == this.techType)
          .pop();
    }

    if (this.caratModel) {
      this.formControls.extraFunctionalities.setValue(this.caratModel.extraFunctionalities || []);
      this.extraFunctionalitiesList.forEach(
        (x) => (x.selected = this.caratModel.extraFunctionalities && this.caratModel.extraFunctionalities.indexOf(x.value) > -1)
      );
      this.formControls.link.setValue(this.caratModel.link);
      this.formControls.productOrServiceDetails.setValue(this.caratModel.productOrServiceDetails);
      this.formControls.describeHowWillBeDelivered.setValue(this.caratModel.describeHowWillBeDelivered);
      this.formControls.maxValueOfTransaction.setValue(this.caratModel.maxValueOfTransaction);
      this.formControls.hasAntiFraudService.setValue(this.caratModel.hasAntiFraudService);
      this.formControls.transactionRiskAware.setValue(this.caratModel.transactionRiskAware);
    }
  }

  protected createFields(): FdFieldConfigs {
    return {
      extraFunctionalities: {
        label: 'Funcionalidades',
        controlName: 'extraFunctionalities',
        messages: {
          required: 'Selecione pelo menos uma funcionalidade',
        },
      },
      link: {
        label: 'Link',
        controlName: 'link',
        maxLength: 250,
        messages: {
          required: 'Informe o link',
        },
      },
      productOrServiceDetails: {
        label: 'Descreva',
        controlName: 'productOrServiceDetails',
        maxLength: 250,
        messages: {
          required: 'Informe a descrição',
        },
      },
      describeHowWillBeDelivered: {
        label: 'Descreva',
        maxLength: 250,
        controlName: 'describeHowWillBeDelivered',
        messages: {
          required: 'Informe a descrição',
        },
      },
      maxValueOfTransaction: {
        label: 'Valor',
        controlName: 'maxValueOfTransaction',
        maxLength: 11,
        messages: {
          required: 'Informe o valor',
          valueTooLow: 'Informe um valor maior que 0',
        },
      },
      hasAntiFraudService: {
        controlName: 'hasAntiFraudService',
        items: [],
        messages: {
          required: 'Informe uma opção',
        },
      },
      transactionRiskAware: {
        controlName: 'transactionRiskAware',
        items: [],
        messages: {
          required: 'Informe uma opção',
        },
      }
    };
  }

  private subscribeToTransactionRiskAware() {
    this.formControls.transactionRiskAware.valueChanges.subscribe((value) => {
      if (!this.transactionRiskAwareModalAccept && value === false) {
        this.dialogService.openDialogWithHtmlMessage(
          this.TRANSACTION_RISK_AWARE_MODAL_MESSAGE,
          () => (this.transactionRiskAwareModalAccept = true),
          'Entendi'
        );
      }
    });
  }
}
